import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { X, Plus, Loader2, Trash2, Info } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { toast } from '@/stores/useToastStore'
import {
  useStoreForecast,
  useSaveStoreForecast,
  useDeleteStoreForecast,
} from '@/hooks/useStoreForecast'
import { useModelGroups } from '@/hooks/useStoreModels'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import type { PlanDetailItem } from '@/types'

interface ForecastModelDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: PlanDetailItem | null
  planId: string
  versionId: string
}

type NumericField =
  | 'total_forecast_units'
  | 'units_per_store'
  | 'units_per_store_per_week'

interface ForecastRow {
  key: number
  store_group_id: string
  total_forecast_units: string
  units_per_store: string
  units_per_store_per_week: string
  source_field: NumericField | null
}

function createBlankRow(key: number): ForecastRow {
  return {
    key,
    store_group_id: '',
    total_forecast_units: '',
    units_per_store: '',
    units_per_store_per_week: '',
    source_field: null,
  }
}

function roundTo(value: number, decimals: number): string {
  const factor = Math.pow(10, decimals)
  return String(Math.round(value * factor) / factor)
}

function recalculateRow(
  sourceField: NumericField,
  sourceValue: string,
  numStores: number | null,
  weeksInPlan: number | null
): {
  total_forecast_units: string
  units_per_store: string
  units_per_store_per_week: string
} {
  const val = sourceValue === '' ? NaN : parseFloat(sourceValue)

  if (isNaN(val)) {
    return {
      total_forecast_units:
        sourceField === 'total_forecast_units' ? sourceValue : '',
      units_per_store: sourceField === 'units_per_store' ? sourceValue : '',
      units_per_store_per_week:
        sourceField === 'units_per_store_per_week' ? sourceValue : '',
    }
  }

  const stores = numStores && numStores > 0 ? numStores : null
  const weeks = weeksInPlan && weeksInPlan > 0 ? weeksInPlan : null

  let total = ''
  let perStore = ''
  let perStorePerWeek = ''

  switch (sourceField) {
    case 'total_forecast_units': {
      total = sourceValue
      if (stores) {
        const upsVal = val / stores
        perStore = roundTo(upsVal, 2)
        if (weeks) {
          perStorePerWeek = roundTo(upsVal / weeks, 2)
        }
      }
      break
    }
    case 'units_per_store': {
      perStore = sourceValue
      if (stores) {
        total = roundTo(val * stores, 2)
      }
      if (weeks) {
        perStorePerWeek = roundTo(val / weeks, 2)
      }
      break
    }
    case 'units_per_store_per_week': {
      perStorePerWeek = sourceValue
      if (weeks) {
        const upsVal = val * weeks
        perStore = roundTo(upsVal, 2)
        if (stores) {
          total = roundTo(upsVal * stores, 2)
        }
      }
      break
    }
  }

  return {
    total_forecast_units: total,
    units_per_store: perStore,
    units_per_store_per_week: perStorePerWeek,
  }
}

export function ForecastModelDrawer({
  open,
  onOpenChange,
  item,
  planId,
  versionId,
}: ForecastModelDrawerProps) {
  const [rows, setRows] = useState<ForecastRow[]>([createBlankRow(0)])
  const [isExistingData, setIsExistingData] = useState(false)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  const keyCounterRef = useRef(1)
  const hasHydratedRef = useRef(false)

  // Fetch store groups from the line item's assigned store model
  const { data: groupsData, isLoading: isLoadingGroups } = useModelGroups(
    open ? (item?.storeModelId ?? undefined) : undefined,
    ''
  )
  const storeGroups = useMemo(() => {
    const allGroups = groupsData?.data ?? []
    const activeIds = item?.activeStoreGroupIds ?? []
    if (activeIds.length === 0) return allGroups
    const activeSet = new Set(activeIds)
    return allGroups.filter(g => activeSet.has(g.id))
  }, [groupsData, item?.activeStoreGroupIds])
  const storeGroupMap = useMemo(
    () => new Map(storeGroups.map(g => [g.id, g])),
    [storeGroups]
  )

  // Fetch forecast data when drawer opens
  const {
    data: forecastData,
    isLoading,
    isFetched,
  } = useStoreForecast(
    open ? planId : undefined,
    open ? versionId : undefined,
    open ? item?.id : undefined
  )

  const weeksInPlan = useMemo(() => {
    const parsed = parseFloat(forecastData?.weeks_in_plan ?? '')
    return isNaN(parsed) || parsed <= 0 ? null : parsed
  }, [forecastData?.weeks_in_plan])

  const saveMutation = useSaveStoreForecast(planId, versionId)
  const deleteMutation = useDeleteStoreForecast(planId, versionId)

  const isSaving = saveMutation.isPending

  // Populate local state from fetched data (only once per drawer open)
  useEffect(() => {
    if (!isFetched || !open || hasHydratedRef.current) return
    hasHydratedRef.current = true

    if (forecastData && forecastData.items && forecastData.items.length > 0) {
      const hydratedRows: ForecastRow[] = forecastData.items.map(entry => {
        const key = keyCounterRef.current++

        let source: NumericField = 'total_forecast_units'
        if (entry.units_per_store_per_week != null) {
          source = 'units_per_store_per_week'
        } else if (entry.units_per_store != null) {
          source = 'units_per_store'
        }

        return {
          key,
          store_group_id: entry.store_group_id,
          total_forecast_units: String(entry.total_forecast_units ?? ''),
          units_per_store: entry.units_per_store ?? '',
          units_per_store_per_week: entry.units_per_store_per_week ?? '',
          source_field: source,
        }
      })
      setRows(hydratedRows)
      setIsExistingData(true)
    } else {
      setRows([createBlankRow(0)])
      keyCounterRef.current = 1
      setIsExistingData(false)
    }
  }, [forecastData, isFetched, open])

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setRows([createBlankRow(0)])
      keyCounterRef.current = 1
      setIsExistingData(false)
      setConfirmRemoveOpen(false)
      hasHydratedRef.current = false
    }
  }, [open])

  const handleAddRow = useCallback(() => {
    const key = keyCounterRef.current++
    setRows(prev => [...prev, createBlankRow(key)])
  }, [])

  const handleRemoveRow = useCallback((key: number) => {
    setRows(prev => {
      if (prev.length <= 1) return prev
      return prev.filter(r => r.key !== key)
    })
  }, [])

  const handleUpdateRow = useCallback(
    (key: number, field: keyof ForecastRow, value: string) => {
      const numericFields: NumericField[] = [
        'total_forecast_units',
        'units_per_store',
        'units_per_store_per_week',
      ]

      setRows(prev =>
        prev.map(row => {
          if (row.key !== key) return row

          if (field === 'store_group_id') {
            const newGroup = storeGroupMap.get(value)
            const newNumStores = newGroup?.member_count ?? null
            const updatedRow = { ...row, store_group_id: value }

            if (updatedRow.source_field) {
              const recalculated = recalculateRow(
                updatedRow.source_field,
                updatedRow[updatedRow.source_field],
                newNumStores,
                weeksInPlan
              )
              return { ...updatedRow, ...recalculated }
            }
            return updatedRow
          }

          if (numericFields.includes(field as NumericField)) {
            const sourceField = field as NumericField
            const group = storeGroupMap.get(row.store_group_id)
            const numStores = group?.member_count ?? null
            const recalculated = recalculateRow(
              sourceField,
              value,
              numStores,
              weeksInPlan
            )
            return { ...row, ...recalculated, source_field: sourceField }
          }

          return { ...row, [field]: value }
        })
      )
    },
    [storeGroupMap, weeksInPlan]
  )

  // Store groups already used (to filter out of dropdown for other rows)
  const usedGroupIds = useMemo(
    () => new Set(rows.map(r => r.store_group_id).filter(Boolean)),
    [rows]
  )

  const handleConfirmRemove = useCallback(() => {
    if (!item) return
    deleteMutation.mutate(
      { lineId: item.id },
      {
        onSuccess: () => {
          setConfirmRemoveOpen(false)
          onOpenChange(false)
        },
      }
    )
  }, [item, deleteMutation, onOpenChange])

  const handleSave = () => {
    if (!item) return

    // Validate: all rows must have a store group selected
    const hasEmptyGroup = rows.some(row => !row.store_group_id)
    if (hasEmptyGroup) {
      toast.error('Each row must have a store group selected')
      return
    }

    // Validate: each row must have a source field with a value
    const hasInvalidRows = rows.some(
      row => !row.source_field || row[row.source_field] === ''
    )

    if (hasInvalidRows) {
      toast.error(
        'Each row must have at least one value: Total Sales Forecast Units, Units per Avg Store, or Units per Avg Store per Avg Week'
      )
      return
    }

    // Send exactly one field per row (the source field the user edited)
    const items = rows.map(row => {
      const sf = row.source_field!
      return {
        store_group_id: row.store_group_id,
        total_forecast_units:
          sf === 'total_forecast_units'
            ? Number(row.total_forecast_units)
            : null,
        units_per_store:
          sf === 'units_per_store' ? Number(row.units_per_store) : null,
        units_per_store_per_week:
          sf === 'units_per_store_per_week'
            ? Number(row.units_per_store_per_week)
            : null,
      }
    })

    saveMutation.mutate(
      { lineId: item.id, body: { items } },
      { onSuccess: () => onOpenChange(false) }
    )
  }

  if (!item) return null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-200 flex-col sm:max-w-200"
      >
        {/* Header */}
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <SheetTitle className="text-lg font-semibold">
                {item.description}
              </SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground">
                SKU #{item.skuNumber}
              </SheetDescription>
              <div>
                <Badge variant="secondary" className="text-xs">
                  Forecast Model
                </Badge>
              </div>
            </div>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-pointer"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading || isLoadingGroups ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-hidden rounded-md border border-[#d6dae1]">
                {/* Table Header */}
                <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_40px] border-b border-[#d6dae1] bg-[#f8f8f8]">
                  <div className="px-3 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Store Group
                  </div>
                  <div className="px-3 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Total Sales Forecast Units
                  </div>
                  <div className="px-3 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Units per Avg Store
                  </div>
                  <div className="px-3 py-2 text-xs font-semibold text-[#1c1c1c]">
                    Units per Avg Store per Avg Week
                  </div>
                  <div />
                </div>

                {/* Table Rows */}
                {rows.map(row => {
                  const availableGroups = storeGroups.filter(
                    g => g.id === row.store_group_id || !usedGroupIds.has(g.id)
                  )

                  return (
                    <div
                      key={row.key}
                      className="grid grid-cols-[1.5fr_1fr_1fr_1fr_40px] border-b border-[#d6dae1] last:border-b-0"
                    >
                      {/* Store Group */}
                      <div className="px-2 py-2">
                        <Select
                          value={row.store_group_id}
                          onValueChange={val =>
                            handleUpdateRow(row.key, 'store_group_id', val)
                          }
                        >
                          <SelectTrigger className="h-8 cursor-pointer rounded-md border-[#d6dae1] text-xs">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableGroups.map(group => (
                              <SelectItem
                                key={group.id}
                                value={group.id}
                                className="cursor-pointer"
                              >
                                {group.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {row.store_group_id &&
                          storeGroupMap.get(row.store_group_id) && (
                            <span className="mt-0.5 block text-[10px] text-muted-foreground">
                              {
                                storeGroupMap.get(row.store_group_id)!
                                  .member_count
                              }{' '}
                              stores
                            </span>
                          )}
                      </div>

                      {/* Total Sales Forecast Units */}
                      <div className="px-2 py-2">
                        <Input
                          type="number"
                          value={row.total_forecast_units}
                          onChange={e =>
                            handleUpdateRow(
                              row.key,
                              'total_forecast_units',
                              e.target.value
                            )
                          }
                          className="h-8 rounded-md border-[#d6dae1] text-xs"
                          placeholder="0"
                          min={0}
                        />
                      </div>

                      {/* Units per Avg Store */}
                      <div className="px-2 py-2">
                        <Input
                          type="number"
                          value={row.units_per_store}
                          onChange={e =>
                            handleUpdateRow(
                              row.key,
                              'units_per_store',
                              e.target.value
                            )
                          }
                          onBlur={e => {
                            const v = parseFloat(e.target.value)
                            if (!isNaN(v)) {
                              handleUpdateRow(
                                row.key,
                                'units_per_store',
                                roundTo(v, 2)
                              )
                            }
                          }}
                          className="h-8 rounded-md border-[#d6dae1] text-xs"
                          placeholder="0"
                          min={0}
                          step="0.01"
                        />
                      </div>

                      {/* Units per Avg Store per Avg Week */}
                      <div className="px-2 py-2">
                        <Input
                          type="number"
                          value={row.units_per_store_per_week}
                          onChange={e =>
                            handleUpdateRow(
                              row.key,
                              'units_per_store_per_week',
                              e.target.value
                            )
                          }
                          onBlur={e => {
                            const v = parseFloat(e.target.value)
                            if (!isNaN(v)) {
                              handleUpdateRow(
                                row.key,
                                'units_per_store_per_week',
                                roundTo(v, 2)
                              )
                            }
                          }}
                          className="h-8 rounded-md border-[#d6dae1] text-xs"
                          placeholder="0"
                          min={0}
                          step="0.01"
                        />
                      </div>

                      {/* Delete */}
                      <div className="flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveRow(row.key)}
                          disabled={rows.length <= 1}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Add Row & Weeks in Plan */}
              <div className="mt-3 flex items-center justify-between">
                <button
                  onClick={handleAddRow}
                  className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-[#CF1F2E] hover:text-[#CF1F2E]/80"
                >
                  <Plus className="h-4 w-4" />
                  Add Row
                </button>
                {weeksInPlan != null && (
                  <span className="text-xs text-muted-foreground">
                    Weeks in Plan:{' '}
                    <span className="font-medium">{weeksInPlan}</span>
                  </span>
                )}
              </div>

              {/* Info Box */}
              <Alert variant="info" className="mt-4">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <p className="mb-1 font-medium">
                    The fields are calculated based on the data provided.
                  </p>
                  <p className="mb-0.5 text-xs font-medium">Formulas:</p>
                  <p className="text-xs italic">
                    Total Sales Forecast Units = Number of Stores &times; Number
                    of Units per Avg Store
                  </p>
                  <p className="text-xs">or</p>
                  <p className="text-xs italic">
                    Total Sales Forecast Units = Number of Stores &times; Number
                    of Units per Avg Store per Week &times; Number of Weeks in
                    Plan
                  </p>
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="border-t px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <div>
              {isExistingData && (
                <button
                  type="button"
                  className="cursor-pointer text-sm text-destructive underline hover:text-destructive/80"
                  onClick={() => setConfirmRemoveOpen(true)}
                >
                  Remove Forecast Model
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className="bg-[#CF1F2E] text-white hover:bg-[#CF1F2E]/90"
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isExistingData
                  ? 'Save Forecast Model'
                  : 'Create Forecast Model'}
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>

      <ConfirmationDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title="Remove Forecast Model"
        description="Are you sure you want to remove the forecast model? This action cannot be undone."
        variant="destructive"
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
        onConfirm={handleConfirmRemove}
      />
    </Sheet>
  )
}
